import { SchedulerConfigurationData } from '../../../../../api';

export interface ResourceCalculation {
  resourcesNeeded: number;
  selectedResources: number[] | null;
  isValid: boolean;
  errorMessage?: string;
}

/**
 * Calculate the number of resources needed based on persons and capacity per resource
 */
export const calculateRequiredResourceCount = (persons: number, personsPerResource: number): number => {
  if (persons <= 0 || personsPerResource <= 0) return 0;
  return Math.ceil(persons / personsPerResource);
};

/**
 * Calculate which resources should be selected based on starting resource and requirements
 */
/**
 * Calculate adjacent resource selection for configurations that require adjacent resources
 */
const calculateAdjacentResourceSelection = (
  startingResourceIndex: number,
  resourcesNeeded: number,
  availableResourceIndexes: number[],
  config: SchedulerConfigurationData,
): ResourceCalculation => {
  const selectedResources: number[] = [];

  // Check if we can fit required adjacent resources from starting point
  for (let i = 0; i < resourcesNeeded; i++) {
    const resourceIndex = startingResourceIndex + i;

    // Check bounds
    if (resourceIndex >= config.resource_count) {
      return {
        resourcesNeeded,
        selectedResources: null,
        isValid: false,
        errorMessage: 'Not enough adjacent resources available from this starting position',
      };
    }

    // Check availability
    if (!availableResourceIndexes.includes(resourceIndex)) {
      return {
        resourcesNeeded,
        selectedResources: null,
        isValid: false,
        errorMessage: `Resource ${resourceIndex + 1} is not available`,
      };
    }

    selectedResources.push(resourceIndex);
  }

  return {
    resourcesNeeded,
    selectedResources,
    isValid: true,
  };
};

/**
 * Calculate flexible resource selection for configurations that don't require adjacent resources
 */
const calculateFlexibleResourceSelection = (
  startingResourceIndex: number,
  resourcesNeeded: number,
  availableResourceIndexes: number[],
): ResourceCalculation => {
  // For flexible selection, we start with the selected resource
  // and let the user choose additional resources manually

  if (!availableResourceIndexes.includes(startingResourceIndex)) {
    return {
      resourcesNeeded,
      selectedResources: null,
      isValid: false,
      errorMessage: `Resource ${startingResourceIndex + 1} is not available`,
    };
  }

  return {
    resourcesNeeded,
    selectedResources: [startingResourceIndex],
    isValid: true,
  };
};

export const calculateResourceSelection = (
  startingResourceIndex: number,
  persons: number,
  config: SchedulerConfigurationData,
  availableResourceIndexes: number[],
): ResourceCalculation => {
  const resourcesNeeded = calculateRequiredResourceCount(persons, config.persons_per_resource);

  if (resourcesNeeded === 0) {
    return {
      resourcesNeeded: 0,
      selectedResources: [],
      isValid: true,
    };
  }

  if (config.require_adjacent_resources) {
    return calculateAdjacentResourceSelection(startingResourceIndex, resourcesNeeded, availableResourceIndexes, config);
  }
  return calculateFlexibleResourceSelection(startingResourceIndex, resourcesNeeded, availableResourceIndexes);
};
